import React, { forwardRef, useState, useEffect, useMemo } from 'react'
import { max } from 'lodash-es'
import styled from '@emotion/styled'
import { LoadingOverlay } from '@mantine/core'
import { useDittoForm, useDitto } from '@ow/ditto-core'
import { listToTree, filterLeaf } from '@ow/utils'
import { ditto } from '../../../ditto/register.field'
import Field from '../field'

const NodeStyled = styled.div`
  border-bottom: 1px solid #dcdcdc;
  .item {
    .node:not(:last-child) {
      border-bottom: 1px solid #dcdcdc;
      border-right: 1px solid #dcdcdc;
    }
  }
  .item:not(:last-child) {
    border-bottom: 1px solid #dcdcdc;
  }
  .item:last-child {
    border-bottom: 0;
  }
`

const renderCloumns = (node, maxLevel, props) => {
  const { editableFields, formState, control, unregister, formControl } = props

  const currentField = editableFields.find(
    (v) =>
      (v.slug.indexOf(node.objectId) > -1 || v.slug === node.slug) &&
      !v.slug.indexOf('_otherSelect') > -1 &&
      !v.slug.indexOf('_otherSelects') > -1
  )

  const { required } = currentField || {}

  const width = (maxLevel - node.level) * 114 + 202
  const widthpx =
    node.children && node.children.length > 0 ? '114px' : width + 'px'
  return (
    <NodeStyled
      className="w-full flex justify-center item items-center"
      style={{
        background: '#EEF4FF',
        minHeight: '42px',
        alignItems: 'stretch',
        flex: 'auto',
      }}
    >
      <div
        style={{
          minWidth: widthpx,
          width: widthpx,
        }}
        className="flex items-center justify-center p-2"
      >
        {node.title}
        {required && (
          <span className="font-semibold text-error-500 ml-4">(必填项*)</span>
        )}
      </div>
      {node.children && node.children.length ? (
        <div
          className="w-full h-full flex items-center justify-center flex-col node"
          style={{
            minHeight: '42px',
            borderLeft: '1px solid #DCDCDC',
            alignItems: 'stretch',
            height: 'auto',
          }}
        >
          {node.children.map((item) => {
            return renderCloumns(item, maxLevel, props)
          })}
        </div>
      ) : (
        <div
          className="w-full h-full flex items-center justify-center flex-col node"
          style={{
            background: '#fff',
            minHeight: '42px',
            alignItems: 'stretch',
            height: 'auto',
            padding: '2px 0',
          }}
        >
          {currentField && (
            <Field
              isInline
              fields={editableFields}
              opts={currentField}
              error={formState.errors[currentField.slug]}
              control={control}
              unregister={unregister}
              formControl={formControl}
              matrix
            />
          )}
        </div>
      )}
    </NodeStyled>
  )
}

const RenderTable = forwardRef((props, ref) => {
  const {
    standards,
    bindStandardIds,
    maxLevel,
    firstCloumns,
    registerFields,
    originalData,
    defaultSlugsValues,
    passValidation,
  } = props

  const {
    control,
    formControl,
    unregister,
    formState,
    editableFields = [],
  } = useDittoForm(
    {
      fields: registerFields, // 只显示绑定的字段外部处理
      data: {
        ...originalData,
        ...defaultSlugsValues,
      },
      ditto,
      passValidation,
    },
    ref
  )

  // 大表上最终展示的指标
  const finalStandards = useMemo(() => {
    // const linkStandardSlugs = editableFields.map((v) => v.slug)

    const treeData = listToTree(standards)

    const arrIds = [...bindStandardIds]
    return filterLeaf(treeData, arrIds)
  }, [bindStandardIds, editableFields, standards])

  return (
    <>
      {[...firstCloumns, ...finalStandards].map((item) => {
        return renderCloumns(item, maxLevel, {
          control,
          formControl,
          unregister,
          formState,
          editableFields,
        })
      })}
    </>
  )
})

/**
 * 大表数据操作
 * @param {*} fields 所有字段
 * @param {*} data 原始数据
 * @param {*} headerFields 表头字段
 * @returns
 */
const WebTableBasic = (
  {
    fields = [],
    data: originalData,
    defaultSlugsValues,
    headerFields,
    filterScope,
    passValidation,
  },
  ref
) => {
  const { onFetchRemoteData } = useDitto()
  const [registerFields, setRegisterFields] = useState(null) // 所有绑定的字段，过滤掉非绑定的，这里没处理好可能会出现校验不通过的问题
  const [bindStandardIds, setBindStandardIds] = useState([]) // 点位绑定的指标
  const [standards, setStandards] = useState([]) // 所有的指标
  const [maxLevel, setMaxLevel] = useState(0)

  const firstCloumns = useMemo(() => {
    return headerFields
      ? headerFields.map((v) => {
          return { ...v, level: 1 }
        })
      : []
  }, [])

  const headerFieldsSlugs = useMemo(() => {
    return headerFields ? headerFields.map((v) => v.slug) : []
  }, [])

  const initStandards = () => {
    // 初始化指标数据
    const standardModel = onFetchRemoteData('standard')
    const promiseArr = [standardModel.fetchEffectiveData(filterScope)]

    let { area } = originalData || {}
    if (!area && defaultSlugsValues) {
      area = defaultSlugsValues.area
    }
    if (area && area.length) {
      const areaId = area[area.length - 1]
      // 获取点位绑定的指标
      promiseArr.push(standardModel.fetchAreaBindStandard(areaId))
      Promise.all(promiseArr).then((result) => {
        const arr = result[0].map((v) => {
          return { ...v, id: v.objectId }
        })

        const level = max(arr.map((v) => v.level))
        setMaxLevel(level)
        setBindStandardIds(result[1])
        setStandards(arr)
      })
    }
  }

  useEffect(() => {
    if (!standards.length) {
      return
    }

    let { area } = originalData || {}
    if (!area && defaultSlugsValues) {
      area = defaultSlugsValues.area
    }
    const allStandardIds = standards.map((v) => v.id)

    // 过滤点位绑定的指标
    if (bindStandardIds.length > 0) {
      setRegisterFields(
        fields.filter((v) => {
          return (
            (v.ownViewDef &&
              v.ownViewDef.isBindStandard &&
              bindStandardIds.some((l) => v.slug.includes(l))) ||
            headerFieldsSlugs.includes(v.slug)
          )
        })
      )
    } else {
      setRegisterFields(
        fields.filter(
          (v) =>
            (v.ownViewDef &&
              v.ownViewDef.isBindStandard &&
              allStandardIds.some((l) => v.slug.includes(l))) ||
            headerFieldsSlugs.includes(v.slug)
        )
      )
    }
  }, [fields, standards, bindStandardIds])

  useEffect(() => {
    // 初始化数据
    initStandards()
  }, [])

  if (!maxLevel || !(registerFields && registerFields.length)) {
    return <LoadingOverlay visible />
  }

  return (
    <div
      className="flex-1 overflow-y-auto"
      style={{
        margin: '14px',
        border: '1px solid #DCDCDC',
        width: '100vm',
        fontSize: '0.8125rem',
        fontWeight: 'bold',
      }}
    >
      <RenderTable
        ref={ref}
        maxLevel={maxLevel}
        standards={standards}
        firstCloumns={firstCloumns}
        bindStandardIds={bindStandardIds}
        registerFields={registerFields}
        originalData={originalData}
        defaultSlugsValues={defaultSlugsValues}
        passValidation={passValidation}
      />
    </div>
  )
}

export default forwardRef(WebTableBasic)
